<template>
  <div class="sa-voice-state-line">
    <p class="sa-voice-state" :class="'sa-is-' + uiState">
      <span class="sa-voice-state-icon" aria-hidden="true">
        <svg
          v-if="uiState == 'busy'"
          class="sa-voice-spin"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
        >
          <path d="M21 12a9 9 0 1 1-6.22-8.56" />
        </svg>
        <svg
          v-else-if="uiState == 'success'"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <svg
          v-else-if="uiState == 'warning'"
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path
            d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
          />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
        <svg
          v-else
          viewBox="0 0 24 24"
          width="20"
          height="20"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
          <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
          <line x1="12" y1="19" x2="12" y2="23" />
        </svg>
      </span>
      <!-- A word as well as an icon and a colour, always. DESIGN.md 7. -->
      <span class="sa-voice-state-word">{{ word }}</span>

      <!-- The countdown. The ring is the glance; the seconds are the answer,
           and they are the half that survives reduced motion and a hidden tab. -->
      <svg
        v-if="showRing"
        class="sa-voice-state-ring"
        viewBox="0 0 24 24"
        width="24"
        height="24"
        aria-hidden="true"
      >
        <circle
          class="sa-voice-state-ring-track"
          cx="12"
          cy="12"
          r="10"
          fill="none"
          stroke-width="2.5"
        />
        <circle
          class="sa-voice-state-ring-arc"
          cx="12"
          cy="12"
          r="10"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          :stroke-dasharray="ringCircumference"
          :stroke-dashoffset="ringOffset"
        />
      </svg>
      <span
        v-if="deadline"
        class="sa-voice-state-seconds"
        aria-live="polite"
        >{{ secondsLabel }}</span
      >
    </p>

    <!-- Why the session ended, on its own line, in the same place every time. -->
    <p v-if="exitCopy" class="sa-voice-state-exit">{{ exitCopy }}</p>
  </div>
</template>

<script>
/**
 * The dock's state line: one word, and the countdown for whichever window is
 * open.
 *
 * The countdown is the point. Five timers used to end a session with nothing on
 * screen to say one was running, so a session that vanished looked like a bug
 * rather than a window closing. This component draws the deadline the session
 * emits, and the sentence it emits on the way out.
 *
 * It owns no timing policy of its own — `deadline` is `{kind, deadlineAt,
 * totalMs}` straight from the session, and everything here is a projection of
 * `Date.now()` onto it. That is deliberate: two clocks disagreeing about how
 * long is left is worse than one clock nobody can see.
 */

const UI_STATES = ["busy", "idle", "success", "warning"];

// r = 10 inside a 24 px box at stroke-width 2.5, so the ring sits just inside
// the same square the state icons occupy.
const RING_RADIUS = 10;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

// Reduced motion and hidden tabs get whole numbers rather than a drawn arc.
// 250 ms is four reads a second: enough that the displayed second is never more
// than a quarter of one stale, cheap enough to leave running.
const FALLBACK_TICK_MS = 250;

export default {
  name: "VoiceStateLine",

  props: {
    /** Listening · Hearing · Understanding · Check this · Applying · Stopped … */
    word: { type: String, default: "" },
    /** `{kind, deadlineAt, totalMs}` from the session, or null when nothing is armed. */
    deadline: { type: Object, default: null },
    /** The session's exit sentence, once it has one. */
    exitCopy: { type: String, default: "" },
    uiState: {
      type: String,
      default: "idle",
      validator: (value) => UI_STATES.indexOf(value) !== -1,
    },
  },

  data() {
    return {
      nowMs: Date.now(),
      // Written only when the whole second changes. The seconds text is a
      // polite live region, and a region rewritten every frame is a screen
      // reader talking over itself.
      secondsLabel: "",
      reducedMotion: false,
      documentHidden: false,
      rafId: null,
      intervalId: null,
    };
  },

  computed: {
    ringCircumference() {
      return RING_CIRCUMFERENCE;
    },

    remainingMs() {
      if (!this.deadline) return 0;
      const deadlineAt = Number(this.deadline.deadlineAt);
      if (!Number.isFinite(deadlineAt)) return 0;
      return Math.max(0, deadlineAt - this.nowMs);
    },

    /**
     * The arc drains: full circle at the start of the window, nothing left at
     * the end. Offset rather than length so the dash pattern stays put and only
     * one attribute moves per frame.
     */
    ringOffset() {
      const totalMs = this.deadline ? Number(this.deadline.totalMs) : 0;
      if (!Number.isFinite(totalMs) || totalMs <= 0) return RING_CIRCUMFERENCE;
      const fraction = Math.min(1, this.remainingMs / totalMs);
      return RING_CIRCUMFERENCE * (1 - fraction);
    },

    /**
     * requestAnimationFrame does not run at all in a hidden tab, so a countdown
     * driven by it would silently freeze and then jump. Reduced motion is the
     * other case, and it wants the same thing for a different reason.
     */
    useFallbackTick() {
      return this.reducedMotion || this.documentHidden;
    },

    showRing() {
      return !!this.deadline && !this.useFallbackTick;
    },
  },

  watch: {
    deadline: "restartTicker",
    useFallbackTick: "restartTicker",
  },

  created() {
    // Before the first render, so a window that opens with a deadline already
    // set never paints an empty seconds slot and then fills it.
    this.tick();
  },

  mounted() {
    this.reducedMotion =
      typeof window.matchMedia == "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.documentHidden = document.hidden === true;
    document.addEventListener("visibilitychange", this.onVisibilityChange);
    this.restartTicker();
  },

  unmounted() {
    this.stopTicker();
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },

  methods: {
    /** Ceil, so the last visible number is "1 s" and never "0 s" for a whole second. */
    secondsText() {
      return `· ${Math.ceil(this.remainingMs / 1000)} s`;
    },

    tick() {
      this.nowMs = Date.now();
      const next = this.secondsText();
      if (next !== this.secondsLabel) this.secondsLabel = next;
    },

    restartTicker() {
      this.stopTicker();
      if (!this.deadline) {
        this.secondsLabel = "";
        return;
      }
      // Paint before the first interval or frame, so a new window shows its
      // real number immediately rather than the previous one's.
      this.tick();
      if (this.useFallbackTick) {
        this.intervalId = setInterval(this.tick, FALLBACK_TICK_MS);
        return;
      }
      this.rafId = requestAnimationFrame(this.frame);
    },

    frame() {
      this.tick();
      this.rafId = requestAnimationFrame(this.frame);
    },

    stopTicker() {
      if (this.rafId != null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      if (this.intervalId != null) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },

    onVisibilityChange() {
      // Only the flag: the `useFallbackTick` watcher swaps the ticker, and it
      // does nothing when the mode has not actually changed.
      this.documentHidden = document.hidden === true;
    },
  },
};
</script>

<style scoped>
/* No Tailwind here. public/assets/styles.css is a committed, pre-compiled
   artefact that the Vue build does not regenerate, so a utility class not
   already in it silently does nothing. Same reasoning as VoiceCommandModal.vue.
   Colours are that file's palette, unchanged. */

.sa-voice-state-line {
  flex: 1 1 auto;
  min-width: 0;
  font-family: Montserrat, sans-serif;
  text-align: left;
}

.sa-voice-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.sa-voice-state-icon {
  flex: none;
  line-height: 0;
}

.sa-voice-state-word {
  min-width: 0;
}

.sa-is-busy {
  color: #0c4a6e;
}
.sa-is-idle {
  color: #0c4a6e;
}
.sa-is-success {
  color: #16a34a;
}
.sa-is-warning {
  color: #a16207;
}

/* The single long-running animation the design system already permits: an
   in-progress spinner, and only while something is genuinely in flight
   (DESIGN.md section 8). */
.sa-voice-spin {
  animation: sa-voice-rotate 900ms linear infinite;
}
@keyframes sa-voice-rotate {
  to {
    transform: rotate(360deg);
  }
}

/* Starts at twelve o'clock rather than three, because a countdown that begins
   somewhere else reads as a progress bar wrapped into a circle. */
.sa-voice-state-ring {
  flex: none;
  margin-left: 0.125rem;
  transform: rotate(-90deg);
}
.sa-voice-state-ring-track {
  stroke: #e2e8f0;
}
/* No transition: the offset is already being written every frame, and a
   transition on top of that would draw the ring a frame behind the number. */
.sa-voice-state-ring-arc {
  transition: none;
}

.sa-voice-state-seconds {
  flex: none;
  /* Tabular figures so the line does not twitch as the count crosses 10. */
  font-variant-numeric: tabular-nums;
  color: #475569;
}

.sa-voice-state-exit {
  margin: 0.25rem 0 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.35;
  color: #475569;
}

@media (prefers-reduced-motion: reduce) {
  .sa-voice-spin {
    animation: none;
  }
}
</style>
